import { FC, useCallback, useEffect, useMemo } from "react";

import {
  ExtendedFindProvider,
  FindBand,
  FindTargetProvider,
  useFindBandShortcut,
} from "@tsmono/react/components";

import { useSelectedEvalSampleData } from "../../state/hooks";
import { useStore } from "../../state/store";

import { InlineSampleDisplay } from "./InlineSampleDisplay";
import styles from "./SampleDetailComponent.module.css";
import {
  NavbarConfig,
  SampleNavbar,
  SampleNavigationConfig,
} from "./SampleNavbar";

export type { NavbarConfig, SampleNavigationConfig };

/**
 * Props for the SampleDetailComponent
 */
export interface SampleDetailComponentProps {
  /** The sample ID from URL params */
  sampleId: string | undefined;
  /** The epoch from URL params */
  epoch: string | undefined;
  /** The tab ID from URL params (for sample tabs like transcript, messages, etc.) */
  tabId: string | undefined;
  /** Navigation configuration for prev/next sample */
  navigation: SampleNavigationConfig;
  /** Navbar configuration for breadcrumb and back navigation */
  navbarConfig: NavbarConfig;
}

/**
 * Shared component for displaying sample details with navigation.
 * Used by both SampleDetailView (for /samples route) and LogSampleDetailView (for /logs route).
 *
 * This component handles:
 * - Keyboard shortcuts (Ctrl+F for find; arrow-key sample nav lives in
 *   SampleNavbar)
 * - Find band integration
 * - Sample tab synchronization (URL → state)
 * - Navigation controls UI (prev/next buttons + sample info)
 * - Sample content rendering via InlineSampleDisplay
 *
 * The parent component is responsible for:
 * - Calculating navigation state
 * - Navigation callbacks
 * - Cleanup on unmount
 */
export const SampleDetailComponent: FC<SampleDetailComponentProps> = ({
  sampleId,
  epoch,
  tabId,
  navigation,
  navbarConfig,
}) => {
  // Sample data and status
  const sampleData = useSelectedEvalSampleData();
  const sample = sampleData.sample;

  // Returns true when sample is undefined (no stale data to worry about —
  // this is normal for running samples, which render streamed events).
  const sampleMatchesRequest = useMemo(() => {
    if (!sampleId || !epoch) return false;
    if (!sample) return true;
    return (
      String(sample.id) === sampleId && sample.epoch === parseInt(epoch, 10)
    );
  }, [sample, sampleId, epoch]);

  // Find functionality
  const showFind = useStore((state) => state.app.showFind);
  const setShowFind = useStore((state) => state.appActions.setShowFind);
  const hideFind = useStore((state) => state.appActions.hideFind);
  const nativeFind = useStore((state) => state.app.nativeFind);

  // Sample tab synchronization
  const setSampleTab = useStore((state) => state.appActions.setSampleTab);

  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    // Set the sample tab if specified in the URL
    if (tabId) {
      setSampleTab(tabId);
    }
  }, [tabId, setSampleTab]);

  const openFind = useCallback(() => setShowFind(true), [setShowFind]);
  useFindBandShortcut(openFind, {
    onClose: hideFind,
    isOpen: showFind,
    enabled: !nativeFind,
  });

  return (
    <ExtendedFindProvider>
      <FindTargetProvider>
        {showFind ? <FindBand onClose={hideFind} /> : ""}
        <div className={styles.detail}>
          <SampleNavbar
            sampleId={sampleId}
            epoch={epoch}
            navigation={navigation}
            navbarConfig={navbarConfig}
          />

          {sampleMatchesRequest && (
            <InlineSampleDisplay className={styles.panel} />
          )}
        </div>
      </FindTargetProvider>
    </ExtendedFindProvider>
  );
};
